import React, { Fragment, useCallback, useEffect, useState } from "react";
import { ModeBadge } from "./ModeBadge";
import axios from "axios";
import { toast } from "sonner";
import {
  TrendingDown, RefreshCw, Download, X, ChevronDown, ChevronRight,
  Gauge, Clock, ShieldOff,
} from "lucide-react";
import { Skeleton } from "./ui/skeleton";
import { CollapseToggle } from "./CollapseToggle";
import { useCollapsed } from "../hooks/useCollapsed";

// 2026-09-08 — Bearish Setup V1 ("PE Opportunity Engine") frontend panel.
// Placed right after Candle Breakout in the Auto Picks tab (chat: "build
// frontend, in auto picks right after CB section"). Mirrors ORB.jsx's
// compact single-file shape (own header/collapse/scan-now, no separate
// paper-trading page) since — like ORB — this is paper-only with no live
// toggle at all (spec section 28: "PE setup must be paper-trading only.
// Do not enable live execution immediately."), so there's no live/paper
// switch or execution-journey detail to show. The open-trade table's
// expandable-row pattern is adapted from CommodityTradeTable.jsx (the
// closest existing precedent for a structure-based, non-live paper
// position with a detail row), inlined here rather than split into a
// separate file since this is a single self-contained section, not a
// multi-page area like Commodities.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (v, d = 2) => (v == null ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: d }));
const fmtPct = (v, d = 2) => {
  if (v == null) return "—";
  const n = Number(v);
  return `${n >= 0 ? "+" : ""}${fmt(n, d)}%`;
};
const pctColor = (v) => (v == null ? "var(--text-muted-2)" : v >= 0 ? "var(--bull)" : "var(--bear)");
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" }) : "—");
// 2026-09-15 (chat: "Bearish PE: Date is missing, add date with time") —
// the Closed Trades table's Entered/Exited columns used fmtTime above,
// which only ever shows "11:20 am" with no date — fine for same-day
// glancing, but trades stay in this table for the retention window (days),
// so a bare time is ambiguous once more than one day's trades are visible.
// Separate from fmtTime (kept as-is for the "Next scan at" indicator,
// which is always today, so a date there would just be clutter).
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", {
  timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
}) : "—");

const REGIME_COLOR = { BEARISH: "var(--bear)", NEUTRAL: "var(--text-muted-2)", BULLISH: "var(--bull)" };

const RegimeBadge = ({ regime, nifty_change_pct }) => {
  const r = regime?.regime || "NEUTRAL";
  const color = REGIME_COLOR[r] || "var(--text-muted-2)";
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 border font-mono-data text-xs uppercase"
         style={{ color, borderColor: color }} data-testid="bearish-pe-regime-badge">
      <Gauge size={12} />
      NIFTY {r}
      {nifty_change_pct != null && (
        <span style={{ color: pctColor(nifty_change_pct) }}>{fmtPct(nifty_change_pct)}</span>
      )}
    </div>
  );
};

const SignalCard = ({ s, idx }) => {
  const opt = s.option || {};
  return (
    <div data-testid={`bearish-pe-signal-${idx + 1}`}
      className="border-grid surface flex flex-col hover:bg-white/[0.02] transition-colors">
      <div className="px-4 py-3 border-b border-grid flex items-center justify-between">
        <div>
          <div className="font-heading text-sm font-semibold">{s.symbol}</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
            {s.sector || "—"}
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-wider px-2 py-1 border font-mono-data"
             style={{ color: "var(--bear)", borderColor: "var(--bear)" }}>
          {s.priority === "HIGH_PRIORITY" ? "HIGH" : "GOOD"} · {fmt(s.final_score, 0)}
        </div>
      </div>
      <div className="px-4 py-2.5 border-b border-grid grid grid-cols-3 gap-2 font-mono-data text-[11px]">
        <div><div className="text-[10px] uppercase tracking-wider text-muted-3">PE</div>
          <div className="font-semibold text-xs mt-0.5">{fmt(opt.strike, 0)} @ ₹{fmt(opt.ltp)}</div></div>
        <div><div className="text-[10px] uppercase tracking-wider text-muted-3">U-Score</div>
          <div className="font-semibold text-xs mt-0.5">{fmt(s.underlying_score, 0)}</div></div>
        <div><div className="text-[10px] uppercase tracking-wider text-muted-3">O-Score</div>
          <div className="font-semibold text-xs mt-0.5">{fmt(opt.option_score, 0)}</div></div>
      </div>
      <div className="px-4 py-2 font-mono-data text-[10px] text-muted-3 flex justify-between">
        <span>RVOL {fmt(s.rvol, 2)}× · RSI {fmt(s.rsi, 0)}</span>
        <span>vs NIFTY <span style={{ color: pctColor(s.relative_weakness) }}>{fmtPct(s.relative_weakness)}</span></span>
      </div>
    </div>
  );
};

const TradeDetailRow = ({ t, colSpan }) => (
  <tr className="border-b border-grid/40" style={{ background: "rgba(255,255,255,0.015)" }}>
    <td colSpan={colSpan} className="px-3 py-2.5">
      <div className="flex flex-wrap gap-x-6 gap-y-1 font-mono-data text-[11px] text-muted-2 pl-9">
        <span>SL (structure) <span className="text-muted-1">{t.invalidation_price != null ? `₹${fmt(t.invalidation_price)}` : "—"}</span></span>
        <span>Target premium <span className="text-muted-1">₹{fmt(t.target_premium)}</span></span>
        <span>R:R <span className="text-muted-1">1:{fmt(t.risk_reward_ratio, 1)}</span></span>
        <span>Lot size <span className="text-muted-1">{t.atm_lot_size ?? "—"}</span> × {t.lots_placed ?? 1}</span>
        <span>RVOL @ entry <span className="text-muted-1">{t.rvol != null ? `${fmt(t.rvol, 2)}x` : "—"}</span></span>
        <span>RSI @ entry <span className="text-muted-1">{fmt(t.rsi, 0)}</span></span>
        <span>Rel. weakness @ entry <span style={{ color: pctColor(t.relative_weakness_at_entry) }}>{fmtPct(t.relative_weakness_at_entry)}</span></span>
        <span>Final score @ entry <span className="text-muted-1">{fmt(t.final_score_at_entry, 0)}</span></span>
        <span>Spread @ entry <span className="text-muted-1">{t.spread_pct_at_entry != null ? `${fmt(t.spread_pct_at_entry)}%` : "—"}</span></span>
        <span>OI @ entry <span className="text-muted-1">{fmt(t.oi_at_entry, 0)}</span></span>
        <span>Expiry <span className="text-muted-1">{t.expiry || "—"}</span></span>
        {t.status === "CLOSED" && (
          <>
            <span>Charges <span className="text-muted-1">{fmt(t.charges_total)}</span></span>
            <span>Net P&amp;L <span style={{ color: pctColor(t.net_pnl) }}>{fmt(t.net_pnl)}</span></span>
          </>
        )}
      </div>
    </td>
  </tr>
);

const TradeRow = ({ t, onExit, colSpan }) => {
  const [open, setOpen] = useState(false);
  return (
    <Fragment>
      <tr className="border-b border-grid/40 hover:bg-white/[0.02]" data-testid="bearish-pe-trade-row">
        <td className="px-3 py-2.5">
          <button onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-2">
            <span className="text-muted-3">{open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</span>
            <span className="font-semibold">{t.symbol}</span>
            <ModeBadge t={t} className="" />
            <span className="text-[9px] uppercase tracking-wider text-muted-3">{t.sector}</span>
          </button>
        </td>
        <td className="px-3 py-2.5 text-muted-2">{t.option_type} {fmt(t.atm_strike, 0)}</td>
        <td className="px-3 py-2.5 text-right text-muted-2">₹{fmt(t.atm_ce_ltp)}</td>
        <td className="px-3 py-2.5 text-right text-muted-2">
          ₹{fmt(t.status === "OPEN" ? t.current_ce_ltp : t.close_ce_ltp)}
        </td>
        <td className="px-3 py-2.5 text-right font-semibold" style={{ color: pctColor(t.pnl_pct) }}>
          {fmtPct(t.pnl_pct)}
        </td>
        <td className="px-3 py-2.5 text-muted-3 font-mono-data text-[10px]">{fmtDateTime(t.entered_at)}</td>
        <td className="px-3 py-2.5 text-muted-3 font-mono-data text-[10px]">
          {t.status === "CLOSED" ? fmtDateTime(t.closed_at) : "—"}
        </td>
        <td className="px-3 py-2.5 text-right">
          {t.status === "OPEN" ? (
            <button onClick={() => onExit(t)} data-testid={`bearish-pe-manual-exit-${t.id}`}
              className="px-2 py-1 border text-[10px] uppercase tracking-wider font-mono-data flex items-center gap-1 ml-auto"
              style={{ color: "var(--bear)", borderColor: "var(--bear)" }}>
              <X size={10} /> Exit
            </button>
          ) : (
            <span className="text-[10px] text-muted-3 font-mono-data uppercase">{t.close_reason || "—"}</span>
          )}
        </td>
      </tr>
      {open && <TradeDetailRow t={t} colSpan={colSpan} />}
    </Fragment>
  );
};

const TradeTable = ({ trades, onExit, emptyLabel, testid }) => {
  const colSpan = 8;
  return (
    <div className="border border-grid overflow-x-auto" data-testid={testid}>
      {!trades || trades.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-muted-2 font-mono-data">{emptyLabel}</div>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted-3 uppercase tracking-wider text-[10px] font-mono-data border-b border-grid">
              <th className="px-3 py-2">Symbol</th>
              <th className="px-3 py-2">Leg</th>
              <th className="px-3 py-2 text-right">Entry</th>
              <th className="px-3 py-2 text-right">{trades[0]?.status === "OPEN" ? "Current" : "Exit"}</th>
              <th className="px-3 py-2 text-right">P&amp;L%</th>
              <th className="px-3 py-2">Entered</th>
              <th className="px-3 py-2">Exited</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {trades.map((t) => <TradeRow key={t.id} t={t} onExit={onExit} colSpan={colSpan} />)}
          </tbody>
        </table>
      )}
    </div>
  );
};

export const BearishPESetup = () => {
  const [data, setData] = useState(null);
  const [closedTrades, setClosedTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [collapsed, toggleCollapsed] = useCollapsed("bearish-pe", false);
  const [showClosed, setShowClosed] = useState(false);
  const [showSignals, setShowSignals] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/bearish-pe`);
      setData(r.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  const loadClosed = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/bearish-pe/paper-trades`);
      const all = [...(r.data?.today || []), ...(r.data?.earlier || [])];
      setClosedTrades(all.filter((t) => t.status === "CLOSED"));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => { if (showClosed) loadClosed(); }, [showClosed, loadClosed]);

  const runScan = async () => {
    setScanning(true);
    try {
      const r = await axios.post(`${API}/bearish-pe/scan`);
      const n = r.data?.run?.signals_count ?? 0;
      toast.success(`Bearish PE scan · ${n} signal${n === 1 ? "" : "s"}`);
      await load();
    } catch { toast.error("Scan failed"); }
    finally { setScanning(false); }
  };

  const onExit = async (t) => {
    try {
      await axios.post(`${API}/bearish-pe/paper-trades/manual-exit`, { trade_id: t.id });
      toast.success(`${t.symbol} closed`);
      await load();
      if (showClosed) await loadClosed();
    } catch { toast.error("Exit failed"); }
  };

  const latestRun = data?.runs?.[0] || null;
  const signals = latestRun?.signals || [];
  const nearMisses = latestRun?.near_misses || [];
  const openTrades = data?.open_trades || [];
  const cfg = data?.config || {};

  // Client-side tally of near-miss rejection reasons -- same diagnostic
  // intent as OI Long Buildup's rejection_summary, computed here since the
  // backend already returns the full per-symbol reason list.
  const rejectionSummary = nearMisses.reduce((acc, r) => {
    const key = (r.reason || "unknown").split(" (")[0];
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  return (
    <section className="space-y-4" data-testid="bearish-pe-section">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <CollapseToggle collapsed={collapsed} onToggle={toggleCollapsed} testId="bearish-pe-collapse" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
              12 / AUTO-PILOT · BEARISH SETUP V1
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
              <TrendingDown size={20} style={{ color: "var(--bear)" }} />
              Bearish PE Opportunity Engine
            </h2>
            <p className="text-xs text-muted-2 mt-1 max-w-3xl" data-testid="bearish-pe-description">
              <strong style={{ color: "var(--text-primary)" }}>PAPER-ONLY</strong> — independent of every
              bullish setup, PE-only. Built to cover the days Candle Breakout / VWAP Bounce Setup B stay
              quiet: a NIFTY regime filter plus a 100-point bearish structure/breakdown score, blended with
              a 100-point real-option-liquidity score (Upstox chain — bid/ask/OI/volume), auto-paper-trades
              signals clearing <strong>{fmt(cfg.final_score_min ?? 75, 0)}/100</strong> blended. Max{" "}
              <strong>{cfg.max_concurrent_trades ?? 3}</strong> concurrent, max{" "}
              <strong>{cfg.max_per_sector ?? 2}</strong> per sector, SL primarily on underlying structure
              invalidation. Scans the <strong>full F&amp;O universe (~210 stocks)</strong> every{" "}
              {data?.scan_interval_min ?? 5} min, {data?.scan_window_ist || "09:25-15:00"} IST. EOD{" "}
              {data?.eod_close_at || "15:15 IST"}.
            </p>
          </div>
        </div>
        {!collapsed && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 border font-mono-data text-xs uppercase"
                  style={{ color: "var(--text-muted-2)", borderColor: "var(--grid)" }}
                  title="No live-trading toggle exists for this setup by design (spec phase 1: paper-only).">
              <ShieldOff size={12} /> Paper-only
            </span>
            {latestRun?.regime && <RegimeBadge regime={latestRun.regime} nifty_change_pct={latestRun.nifty_change_pct} />}
            <button onClick={runScan} disabled={scanning} data-testid="bearish-pe-scan-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-grid font-mono-data text-xs uppercase hover:bg-white/5">
              <RefreshCw size={12} className={scanning ? "animate-spin" : ""} />
              {scanning ? "Scanning…" : "Scan Now"}
            </button>
            <a href={`${API}/bearish-pe/export.csv`} data-testid="bearish-pe-export-csv"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-grid font-mono-data text-xs uppercase hover:bg-white/5">
              <Download size={12} /> Export CSV
            </a>
          </div>
        )}
      </div>

      {!collapsed && (
        <>
          {loading && !data ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-40" />)}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 font-mono-data text-xs">
                <div className="border-grid surface px-4 py-3">
                  <div className="text-[10px] uppercase tracking-wider text-muted-3">Open Positions</div>
                  <div className="text-lg font-semibold mt-1">{data?.open_count ?? 0} / {cfg.max_concurrent_trades ?? 3}</div>
                </div>
                <div className="border-grid surface px-4 py-3">
                  <div className="text-[10px] uppercase tracking-wider text-muted-3">Latest Scan</div>
                  <div className="text-lg font-semibold mt-1">{latestRun?.scanned ?? "—"} / {latestRun?.universe_size ?? "—"}</div>
                </div>
                <div className="border-grid surface px-4 py-3">
                  <div className="text-[10px] uppercase tracking-wider text-muted-3">Signals This Run</div>
                  <div className="text-lg font-semibold mt-1">{signals.length}</div>
                </div>
                <div className="border-grid surface px-4 py-3">
                  <div className="text-[10px] uppercase tracking-wider text-muted-3">Next Scan</div>
                  <div className="text-sm font-semibold mt-1.5 flex items-center gap-1">
                    <Clock size={12} className="text-muted-3" /> {data?.next_scan_at ? fmtTime(data.next_scan_at) : "—"}
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xs uppercase tracking-wider text-muted-3 font-mono-data mb-2">Open Paper Trades</h3>
                <TradeTable trades={openTrades} onExit={onExit} testid="bearish-pe-open-trades"
                  emptyLabel={`No open positions — ${latestRun ? "latest scan found nothing above the score threshold." : "waiting for the next scan."}`} />
              </div>

              {signals.length > 0 && (
                <div>
                  <button onClick={() => setShowSignals((v) => !v)} data-testid="bearish-pe-toggle-signals"
                    className="text-xs uppercase tracking-wider text-muted-3 font-mono-data mb-2 hover:text-muted-1 flex items-center gap-1">
                    {showSignals ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    Latest Scan Signals ({signals.length}) ({latestRun?.ran_at_ist || "—"})
                  </button>
                  {showSignals && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {signals.map((s, i) => <SignalCard key={s.symbol} s={s} idx={i} />)}
                    </div>
                  )}
                </div>
              )}

              {Object.keys(rejectionSummary).length > 0 && (
                <div className="border-grid surface p-4">
                  <h3 className="text-xs uppercase tracking-wider text-muted-3 font-mono-data mb-2">
                    Why the rest didn't qualify ({nearMisses.length} scanned, not signaled)
                  </h3>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 font-mono-data text-[11px] text-muted-2">
                    {Object.entries(rejectionSummary).sort((a, b) => b[1] - a[1]).map(([reason, count]) => (
                      <span key={reason}>{reason} <span className="text-muted-1">×{count}</span></span>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <button onClick={() => setShowClosed((v) => !v)} data-testid="bearish-pe-toggle-closed"
                  className="text-xs uppercase tracking-wider text-muted-3 font-mono-data mb-2 hover:text-muted-1 flex items-center gap-1">
                  {showClosed ? <ChevronDown size={12} /> : <ChevronRight size={12} />} Closed Trades
                </button>
                {showClosed && (
                  <TradeTable trades={closedTrades} testid="bearish-pe-closed-trades"
                    emptyLabel="No closed trades yet." />
                )}
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
};

export default BearishPESetup;
